import React, { useMemo, useRef, useState, useEffect, useCallback, startTransition } from 'react'
import classNames from 'classnames'
import { Maybe } from 'graphql/jsutils/Maybe'
import dynamic from 'next/dynamic'
import { When } from 'react-if'
import { PaddedContainer } from '@/atoms/PaddedContainer'
import { slugs } from '@/constants/slugs'
import { AppFeatures } from '@/experimentation'
import { BackgroundPosition, WatchProjectHero } from '@/organisms/WatchProjectHero'
import { WatchProjectTabs } from '@/organisms/WatchProjectTabs'
import { LightFranchise } from '@/services/FranchiseService'
import { ContentWithWatchableReasons, isSellingTickets } from '@/services/ProjectPhaseService'
import {
  getIsGeoblocked,
  Project,
  ProjectMeta,
  ProjectType,
  ResumeWatchingData,
  Season,
  SeasonLinkMeta,
} from '@/services/ProjectsService'
import { useWatchProjectContext } from '@/services/ProjectsService/WatchProjectProvider'
import { SuggestedShowsAndFilmsContent } from '@/services/RecommendationService'
import { PageDataContext, PageModel } from '@/services/RenderService'
import { ShopifyConfiguration } from '@/services/ShopifyConfigurationService/ShopifyConfigurationService'
import { Story } from '@/services/StoriesService'
import { ProjectTheme } from '@/types/codegen-contentful'
import { processWatchablesToMediasWithFiltering } from '@/utils/EpisodeUtil'
import { useLocale } from '@/utils/LocaleUtil'
import { logger } from '@/utils/logging'
import { getProjectTheme } from '@/utils/project-themes'
import { useTranslate } from '@/utils/translate/translate-client'
import { useWatchProjectData } from '@/views/WatchProjectView/useWatchProjectData'
import { useGuildTickets } from '../TicketCheckoutViews/hooks/useGuildTickets'
import { WatchProjectContext } from './WatchProjectContext'
import { useFadeBackgroundOnScroll } from './useFadeBackgroundOnScroll'
import { getMovieEpisodeSharePath } from './utils'

// Dynamic imports for below-the-fold components to reduce initial bundle size
const FaqSection = dynamic(() => import('@/organisms/FaqSection').then((mod) => mod.FaqSection), { ssr: true })
const AppPromoSection = dynamic(
  () => import('@/views/LandingView/AppPromoSection').then((mod) => mod.AppPromoSection),
  { ssr: true },
)
const WatchProjectShareSeriesModal = dynamic(
  () => import('@/views/WatchProjectView/WatchProjectShareSeriesModal').then((mod) => mod.WatchProjectShareSeriesModal),
  { ssr: false },
)
const FranchiseFilmSeriesCTA = dynamic(
  () => import('./FranchiseFilmSeriesCTA').then((mod) => mod.FranchiseFilmSeriesCTA),
  { ssr: true },
)

/**
 * Type for projectData prop that accepts either Project or normalized ProjectMeta.
 * Both have the same core fields needed for rendering the watch page.
 * When using getProjectAbout with includeSeasons: true, the data has the same
 * runtime structure as Project but TypeScript sees it as ProjectMeta.
 */
export type WatchProjectData = Project | (ProjectMeta & { seasons: Season[] })

/**
 * Type for season edge nodes derived from Project.title.seasons.edges.
 * Used when iterating over seasons to check for episodes.
 */
type TitleSeasonEdge = NonNullable<NonNullable<Project['title']>['seasons']>['edges'] extends (infer E)[] | undefined
  ? E
  : never

/**
 * Common project fields shared between Project and ProjectMeta.
 * Used to create a properly typed object without unsafe assertions.
 */
type CommonProjectFields = Pick<
  Project,
  | 'slug'
  | 'name'
  | 'projectType'
  | 'logoCloudinaryPath'
  | 'discoveryPosterLandscapeCloudinaryPath'
  | 'pifEnabled'
  | 'id'
  | 'seasons'
  | 'videos'
  | 'franchiseDetails'
  | 'highestScore'
  | 'title'
>

/**
 * Helper to safely convert WatchProjectData to Project for child components.
 * Constructs a new object with common fields, avoiding unsafe type assertions.
 * This leverages structural typing - since WatchProjectData has all the
 * required Project fields, we can safely construct a Project-shaped object.
 */
const asProject = (data: WatchProjectData): Project => {
  // Both Project and ProjectMeta share these fields with compatible types.
  // We construct a new object to satisfy TypeScript's structural typing.
  const project: CommonProjectFields = {
    slug: data.slug,
    name: data.name,
    projectType: data.projectType,
    logoCloudinaryPath: data.logoCloudinaryPath,
    discoveryPosterLandscapeCloudinaryPath: data.discoveryPosterLandscapeCloudinaryPath,
    pifEnabled: data.pifEnabled,
    id: data.id,
    seasons: data.seasons,
    videos: data.videos,
    franchiseDetails: data.franchiseDetails,
    highestScore: data.highestScore,
    // The title field structure differs, but we pass it through as-is.
    // Child components access common properties that exist on both types.
    title: data.title as Project['title'],
  }
  return project
}

export interface WatchProjectViewProps {
  currentlyFunding?: boolean
  discoveryPosterLandscapeCloudinaryPath: string
  name: string
  projectType: ProjectType
  shopifyConfig?: ShopifyConfiguration | null
  singleStory?: boolean
  slug: string
  story?: Story
  theaterBackgroundPosition?: BackgroundPosition
  seasons?: SeasonLinkMeta[]
  pageDataContext: Maybe<PageDataContext>
  page: Maybe<PageModel>
  contentfulProjectTheme: Maybe<ProjectTheme>
  isProjectInEarlyAccess?: boolean
  projectData: WatchProjectData
  isGuildMember?: boolean
  isLoggedIn?: boolean
  franchise?: LightFranchise
  hasWatchableReasons?: boolean
  activeSweepstake?: boolean
  skipsEnabled?: boolean
  experiments?: Record<keyof AppFeatures, unknown>
  suggestedShowsAndFilms?: SuggestedShowsAndFilmsContent
  resumeWatchingData?: ResumeWatchingData | null
  watchableContent?: ContentWithWatchableReasons | null
}

export const WatchProjectView: React.FC<WatchProjectViewProps> = (props) => {
  const {
    discoveryPosterLandscapeCloudinaryPath,
    name,
    projectType,
    singleStory,
    story,
    slug,
    theaterBackgroundPosition,
    seasons = [],
    page,
    pageDataContext,
    contentfulProjectTheme,
    isProjectInEarlyAccess = false,
    projectData,
    shopifyConfig,
    franchise,
    hasWatchableReasons = false,
    activeSweepstake = false,
    skipsEnabled = false,
    experiments,
    isGuildMember = false,
    suggestedShowsAndFilms = [],
    resumeWatchingData = null,
  } = props
  const { t } = useTranslate('watch')
  const { locale } = useLocale()
  const { watchables, ticketStatus, watchableContent, guildPermissions } = useWatchProjectContext()
  const [primaryCtaGuid, setPrimaryCtaGuid] = useState<string | null>(null)
  const backgroundRef = useRef<HTMLDivElement>(null)
  const boundaryRef = useRef<HTMLDivElement>(null)
  const faqsRef = useRef<HTMLDivElement>(null)
  const [userHasScrolled, setUserHasScrolled] = useState(false)
  useFadeBackgroundOnScroll({ backgroundRef, boundaryRef })
  const isGeoblockedProject = getIsGeoblocked(projectData)
  const canShowTickets = isSellingTickets(ticketStatus)
  // Access watchableReasons with type guard since it may not exist on all title types
  const watchableReasons = useMemo(() => {
    const title = projectData?.title
    if (title && 'watchableReasons' in title) {
      return title.watchableReasons ?? []
    }
    return []
  }, [projectData?.title])

  const title = projectData?.title
  const showDescription = useMemo(() => {
    const medium =
      title && 'mediumDescription' in title && typeof title.mediumDescription === 'string'
        ? title.mediumDescription.trim()
        : ''
    const short =
      title && 'shortDescription' in title && typeof title.shortDescription === 'string'
        ? title.shortDescription.trim()
        : ''

    if (medium) return medium
    if (short) return short
  }, [title])

  const ticketsDefaultData = { discountCodes: [], areGuildTicketsAvailable: false }

  const ticketsHookResult = useGuildTickets(canShowTickets ? slug : '', {
    isGuildMember,
    guildPermissions,
  })

  const { discountCodes, areGuildTicketsAvailable } = canShowTickets ? ticketsHookResult : ticketsDefaultData

  const handlePrimaryCtaGuidChange = useCallback((guid: string | null) => {
    setPrimaryCtaGuid(guid)
  }, [])

  useEffect(() => {
    const PROJECT_LOGO_SCROLL_THRESHOLD = 210
    let ticking = false

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const shouldShow = window.scrollY > PROJECT_LOGO_SCROLL_THRESHOLD
          startTransition(() => {
            setUserHasScrolled((prev) => (prev !== shouldShow ? shouldShow : prev))
          })
          ticking = false
        })
        ticking = true
      }
    }

    if (typeof window !== 'undefined') {
      // Delay the initial scroll check to avoid hydration mismatch
      const timeoutId = setTimeout(() => {
        handleScroll()
      }, 0)
      window.addEventListener('scroll', handleScroll, { passive: true })

      return () => {
        clearTimeout(timeoutId)
        window.removeEventListener('scroll', handleScroll)
      }
    }
  }, [])

  useWatchProjectData({
    data: asProject(projectData),
    skip: !seasons?.length,
    isGuildMember,
    isGeoblockedProject,
    resumeWatchingData: resumeWatchingData ?? undefined,
    streamingContent: watchableContent,
  })
  const projectTheme = getProjectTheme(slug) ?? {}
  const showFranchiseFilmSeriesCTA = false

  const hasEpisodesToShow = useMemo(() => {
    if (projectData?.projectType === 'movie' || projectData?.projectType === 'special') {
      return false
    }

    // Access title.seasons with type guard since structure varies by title type
    const title = projectData?.title
    if (title && 'seasons' in title && title.seasons) {
      const titleSeasons = title.seasons.edges
      if (titleSeasons && titleSeasons.length > 0) {
        const hasAnyEpisodes = titleSeasons.some((seasonEdge: TitleSeasonEdge | null) => {
          const episodeCount = seasonEdge?.node?.episodes?.edges?.length ?? 0
          return episodeCount > 0
        })
        return hasAnyEpisodes
      }
    }

    return false
  }, [projectData])

  const isMovieOrSpecial = !hasEpisodesToShow

  // Process watchables for movies/specials into media format
  const movieSpecialMedias = useMemo(() => {
    if (!watchables || watchables.length === 0) {
      return []
    }

    try {
      return processWatchablesToMediasWithFiltering(
        watchables,
        {
          projectName: projectData?.name,
          hasEarlyAccess: isProjectInEarlyAccess,
          projectType: projectData?.projectType,
          trailerText: t('trailer', 'Trailer'),
          locale,
        },
        isGeoblockedProject,
        isGuildMember,
      )
    } catch (error) {
      logger().error('Failed to process watchables', { slug, projectName: projectData?.name }, error)
      return []
    }
  }, [
    watchables,
    projectData?.projectType,
    projectData?.name,
    t,
    locale,
    isGeoblockedProject,
    isGuildMember,
    isProjectInEarlyAccess,
    slug,
  ])

  const shareGuid = useMemo(() => {
    if (projectType === 'series') {
      // Prefer the first season's first episode id if available via title.seasons connection
      const title = projectData?.title
      if (title && 'seasons' in title && title.seasons) {
        const firstSeason = title.seasons.edges?.[0]?.node
        const firstEpisodeId = firstSeason?.episodes?.edges?.[0]?.node?.id
        return firstEpisodeId ?? null
      }
      return null
    }
    // Special: filter for items with watchableType === "SPECIAL"
    if (projectType === 'special') {
      const specialMedias = movieSpecialMedias.filter((m) => m.watchableType === 'SPECIAL')
      const firstWithGuid = specialMedias.find((m) => Boolean(m.guid))
      return firstWithGuid?.guid ?? null
    }
    // Movie: filter for items with watchableType === "MOVIE"
    if (projectType === 'movie') {
      const movieMedias = movieSpecialMedias.filter((m) => m.watchableType === 'MOVIE')
      const firstWithGuid = movieMedias.find((m) => Boolean(m.guid))
      return firstWithGuid?.guid ?? null
    }
    return null
  }, [projectType, projectData?.title, movieSpecialMedias])

  const shareCtaPath = getMovieEpisodeSharePath({
    slug,
    guid: shareGuid,
    projectType,
    defaultCtaPath: '',
  })

  const showParentalControlsExperiment =
    slug === slugs.sketch ? true : experiments ? Boolean(experiments['family-friendly-audio-support']) : false

  return (
    <WatchProjectContext.Provider value={{ projectTheme, project: projectData }}>
      <div className={classNames('overflow-x-hidden bg-core-gray-950 text-white')}>
        <main>
          <WatchProjectHero
            className="mb-12"
            cloudinaryImgPath={
              discoveryPosterLandscapeCloudinaryPath
                ? `${discoveryPosterLandscapeCloudinaryPath}.webp`
                : '/v1706130971/angel-app/angel-studios/discovery_images/Theater-Home-Page-Banner_v2_2024-01-16_1920x1080.webp'
            }
            showTitle={name}
            slug={slug}
            theaterBackgroundPosition={theaterBackgroundPosition}
            isGuildMemberContent={isProjectInEarlyAccess}
            shopifyConfig={shopifyConfig}
            contentfulProjectTheme={contentfulProjectTheme}
            guildScore={projectData?.highestScore}
            isGuildMember={isGuildMember}
            isGeoBlockedProject={isGeoblockedProject}
            projectData={asProject(projectData)}
            shareCtaPath={shareCtaPath}
            watchableReasons={watchableReasons}
            guildCodes={discountCodes}
            areGuildTicketsAvailable={areGuildTicketsAvailable}
            activeSweepstake={activeSweepstake}
            showParentalControlsExperiment={showParentalControlsExperiment}
            skipsEnabled={skipsEnabled}
            movieSpecialMedias={movieSpecialMedias}
            darkenBackground={userHasScrolled}
            onPrimaryCtaGuidChange={handlePrimaryCtaGuidChange}
            showDescription={showDescription}
            resumeWatchingData={resumeWatchingData ?? undefined}
          />
        </main>
        <div ref={boundaryRef}>
          <WatchProjectTabs
            projectData={asProject(projectData)}
            loadingProjectData={false}
            slug={slug}
            singleStory={singleStory}
            story={story}
            page={page}
            pageDataContext={pageDataContext}
            isGuildMember={isGuildMember}
            franchise={franchise}
            hasWatchableReasons={hasWatchableReasons}
            isGeoblockedProject={isGeoblockedProject}
            movieSpecialMedias={movieSpecialMedias}
            showProjectLogo={userHasScrolled}
            primaryCtaGuid={primaryCtaGuid}
            isMovieOrSpecial={isMovieOrSpecial}
            suggestedShowsAndFilms={suggestedShowsAndFilms}
          />
        </div>
        <div className="relative h-48 w-full md:h-40">
          {/* Bottom -> Top Gradient  */}
          <div
            className={classNames('absolute inset-0 left-0 bottom-0 h-full w-full transition-opacity')}
            style={{
              background: 'linear-gradient(0deg, #141414 0%, rgba(20, 20, 20, 0.00) 100%)',
            }}
          />
        </div>
        <section id="faqs" className="relative scroll-mt-[73px] bg-core-gray-950 pb-40 pt-8 md:pb-20">
          <div ref={faqsRef}>
            <PaddedContainer fluid className="relative z-10">
              <FaqSection />
            </PaddedContainer>
          </div>
        </section>
        <AppPromoSection
          containerClassName="pt-10"
          overrideAppImageUrl={'/v1715792164/angel-studios/landing/watch_amytime_-_image.webp'}
        />
        {showFranchiseFilmSeriesCTA && isGuildMember && (
          <FranchiseFilmSeriesCTA project={asProject(projectData)} franchise={franchise} />
        )}
      </div>
      <When condition={Boolean(isGuildMember && projectType === 'series')}>
        <WatchProjectShareSeriesModal slug={slug} project={asProject(projectData)} />
      </When>
    </WatchProjectContext.Provider>
  )
}
